import { HELP_CENTRE_URL } from "@/constants/brand";

/**
 * Help Centre article URLs keyed by the slug the University importer produced
 * when it rewrote `docs/help-centre/NN-slug.md` → `#lesson:slug`.
 *
 * Prefer matching a real course lesson first; use this map only as a fallback.
 */
const HELP_CENTRE_ARTICLE_BY_SLUG: Record<string, string> = {
  "who-is-tessera-for": `${HELP_CENTRE_URL}/getting-started/who-is-tessera-for`,
  "understanding-changes": `${HELP_CENTRE_URL}/getting-started/understanding-when-results-change`,
  "understanding-when-results-change": `${HELP_CENTRE_URL}/getting-started/understanding-when-results-change`,
  "subscription-and-access": `${HELP_CENTRE_URL}/billing/subscription-and-access`,

  "trade-log": `${HELP_CENTRE_URL}/trade-log/trade-log`,
  "timezones-and-dates": `${HELP_CENTRE_URL}/trade-log/timezones-and-dates`,

  "brokers-overview": `${HELP_CENTRE_URL}/brokers-and-data/brokers-overview`,
  "broker-data-and-sync": `${HELP_CENTRE_URL}/brokers-and-data/broker-data-and-sync`,
  "custom-csv-import": `${HELP_CENTRE_URL}/brokers-and-data/custom-csv-import`,
  instruments: `${HELP_CENTRE_URL}/brokers-and-data/instruments`,
  "market-data": `${HELP_CENTRE_URL}/brokers-and-data/market-data`,
  "connect-tradingview": `${HELP_CENTRE_URL}/brokers-and-data/connect-tradingview`,
  "connect-tradingview-paper": `${HELP_CENTRE_URL}/brokers-and-data/connect-tradingview-paper`,
  "connect-liquid-brokers": `${HELP_CENTRE_URL}/brokers-and-data/connect-liquid-brokers`,
  "connect-oanda": `${HELP_CENTRE_URL}/brokers-and-data/connect-oanda`,
  "connect-binance": `${HELP_CENTRE_URL}/brokers-and-data/connect-binance`,
  "connect-ibkr": `${HELP_CENTRE_URL}/brokers-and-data/connect-ibkr`,
  "connect-ctrader": `${HELP_CENTRE_URL}/brokers-and-data/connect-ctrader`,
  "connect-mt4": `${HELP_CENTRE_URL}/brokers-and-data/connect-mt4`,
  "connect-mt5": `${HELP_CENTRE_URL}/brokers-and-data/connect-mt5`,
  "connect-tradovate": `${HELP_CENTRE_URL}/brokers-and-data/connect-tradovate`,
  "connect-ninjatrader": `${HELP_CENTRE_URL}/brokers-and-data/connect-ninjatrader`,
  "connect-hyperliquid": `${HELP_CENTRE_URL}/brokers-and-data/connect-hyperliquid`,

  reports: `${HELP_CENTRE_URL}/reports/reports`,
  "trade-review": `${HELP_CENTRE_URL}/reports/trade-review`,
  "tes-ai-analysis": `${HELP_CENTRE_URL}/reports/tes-ai-analysis`,
  "notes-on-reports": `${HELP_CENTRE_URL}/reports/notes-on-reports`,

  "strategies-and-policies": `${HELP_CENTRE_URL}/strategies/strategies-and-policies`,
  "policy-pine-tradingview": `${HELP_CENTRE_URL}/strategies/policy-pine-tradingview`,

  "simulator-overview": `${HELP_CENTRE_URL}/simulator/simulator-overview`,
  "simulator-conditions-and-parameters": `${HELP_CENTRE_URL}/simulator/simulator-conditions-and-parameters`,

  "custom-dashboard": `${HELP_CENTRE_URL}/dashboard/custom-dashboard`,
  "portfolio-growth": `${HELP_CENTRE_URL}/dashboard/portfolio-growth`,
  milestones: `${HELP_CENTRE_URL}/dashboard/milestones`,
  notifications: `${HELP_CENTRE_URL}/dashboard/notifications`,

  statistics: `${HELP_CENTRE_URL}/statistics/statistics`,

  "workspace-overview": `${HELP_CENTRE_URL}/workspace/workspace-overview`,
  "workspace-nodes-and-policy-types": `${HELP_CENTRE_URL}/workspace/workspace-nodes-and-policy-types`,
};

function slugFromHelpCentrePath(url: string): string | null {
  const match = url.trim().match(/(?:^|\/)(?:help-centre\/)?(?:\d+-)?([a-z0-9-]+)\.md(?:#.*)?$/i);
  if (!match?.[1]) return null;
  return match[1].toLowerCase();
}

/** Resolve a University Portable Text href to a Help Centre article URL when applicable. */
export function resolveHelpCentreHref(href: string): string | null {
  const trimmed = (href || "").trim();
  if (!trimmed) return null;

  if (/^https?:\/\/help\.tesseratrades\.com\//i.test(trimmed)) {
    return trimmed;
  }

  const hashLesson = trimmed.match(/^#lesson:([a-z0-9-]+)$/i);
  if (hashLesson?.[1]) {
    return HELP_CENTRE_ARTICLE_BY_SLUG[hashLesson[1].toLowerCase()] ?? null;
  }

  const fromPath = slugFromHelpCentrePath(trimmed);
  if (fromPath && /help-centre\//i.test(trimmed)) {
    return HELP_CENTRE_ARTICLE_BY_SLUG[fromPath] ?? null;
  }

  return null;
}
